/**
 * UserPlateView — Per-user "what's on their plate" homepage variant.
 * Renders when an admin picks a user via UserPlatePicker. Filters the
 * existing dashboard data through stage-aware role buckets (see platePeople).
 *
 * IMPORTANT: This is HOME-LOCAL personalization. It does NOT change
 * permissions; admin still has full data access on Jobs/Calendar/Payroll.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Paper, Typography, Grid, Chip, Divider, Link, Alert } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useJobs } from '../../hooks/useJobs';
import { useProfiles } from '../../hooks/useProfiles';
import { useRoster } from '../../hooks/useRoster';
import { useTimeEntries } from '../../hooks/useTimeEntries';
import { usePayPeriods } from '../../hooks/usePayroll';
import { resolveTargetIds, isOnPlate } from '../../lib/platePeople';
import { normalizeSnapshot, getAllEarnings } from '../../lib/calcEngine';
import { computeOpenPeriodDates } from '../../lib/payPeriodEngine';
import { fmtC, STATUS_COLORS, toLocalDateStr } from '../../lib/utils';
import { navigateToJob } from '../../lib/crossNav';
import { api } from '../../lib/api';

const ACTIVE_STATUSES = ['intake', 'quoted', 'confirmed', 'shooting', 'post_production', 'partial_delivery', 'delivered', 'invoiced'];

const monoSx = { fontFamily: '"DM Mono", monospace' };

// Earning categories the user can show up in. Matches the role/type fields
// returned by calcEngine.getAllEarnings().
const CATEGORY_DEFS = [
  { key: 'orig',        label: 'Originator',          color: '#F472B6' },
  { key: 'prod_lead',   label: 'Production · Lead',   color: '#13B5EA' },
  { key: 'prod_hourly', label: 'Production · Hourly', color: '#60A5FA' },
  { key: 'post_lead',   label: 'Post · Lead',         color: '#10B981' },
  { key: 'post_hourly', label: 'Post · Hourly',       color: '#34D399' },
];

/**
 * Earnings owed to a person on a single job, summed across all roles.
 * Uses normalized snapshot + getAllEarnings (the canonical reader).
 */
function earningsForOnJob(job, targetName) {
  if (!job?.calcSnapshot || !targetName) return 0;
  let snap;
  try {
    snap = typeof job.calcSnapshot === 'string' ? JSON.parse(job.calcSnapshot) : job.calcSnapshot;
    snap = normalizeSnapshot(snap);
  } catch {
    return 0;
  }
  const lc = targetName.toLowerCase();
  return getAllEarnings(snap)
    .filter(e => (e.name || '').toLowerCase() === lc)
    .reduce((s, e) => s + (e.earned || 0), 0);
}

export default function UserPlateView({ userId }) {
  const navigate = useNavigate();
  const { data: profiles = [] } = useProfiles();
  const { data: roster = [] } = useRoster();
  const { data: jobs = [] } = useJobs();
  const { data: timeEntries = [] } = useTimeEntries();
  const { data: payPeriods = [] } = usePayPeriods();

  const profile = useMemo(() => profiles.find(p => p.id === userId), [profiles, userId]);
  const target = useMemo(() => resolveTargetIds(profile, roster), [profile, roster]);

  const todayStr = toLocalDateStr(new Date());

  // ── Resolve current pay period (open one, or most recent closed) ──────
  const currentPeriod = useMemo(() => {
    if (!payPeriods.length) {
      const { startDate, endDate } = computeOpenPeriodDates([]);
      return { startDate, endDate, status: 'open', id: null };
    }
    const open = payPeriods.find(p => p.status === 'open');
    if (open) return open;
    const archived = payPeriods.filter(p => p.status === 'archived');
    const sorted = [...archived].sort((a, b) =>
      (b.endDate || '').localeCompare(a.endDate || '')
    );
    return sorted[0] || payPeriods[0];
  }, [payPeriods]);

  const periodIsOpen = currentPeriod?.status === 'open' || !currentPeriod?.id;

  // ── Earned (so far) — ledger rows where trigger has fired (work done) ─
  // Authoritative: state='earned' means the role's trigger fired (originator
  // → job paid + fully paid by client; prod → status ≥ post_production;
  // post → status ≥ delivered). Includes earned-and-paid history too so the
  // number reflects total earnings the user has booked, not just open AR.
  const { data: ledgerRows = [] } = useQuery({
    queryKey: ['job-earnings-ledger', 'plate', target.name],
    queryFn: () => api.get(`/job-earnings-ledger?person=${encodeURIComponent(target.name)}`),
    enabled: !!target.name,
  });
  const earnedSoFar = useMemo(() => {
    const lc = (target.name || '').toLowerCase();
    return ledgerRows
      .filter(r => (r.person || '').toLowerCase() === lc)
      .filter(r => r.state === 'earned' || r.state === 'paid')
      .reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  }, [ledgerRows, target.name]);

  // ── Plate-filtered job lists ──────────────────────────────────────────
  const plateJobs = useMemo(() => {
    if (!target.name && !target.rosterId) return [];
    return jobs.filter(j => isOnPlate(j, target));
  }, [jobs, target]);

  const activePlateJobs = useMemo(
    () => plateJobs.filter(j => ACTIVE_STATUSES.includes(j.status)),
    [plateJobs]
  );

  // ── Stat: Hours This Period (target's time entries in period) ─────────
  const hoursThisPeriod = useMemo(() => {
    if (!target.name) return 0;
    const lc = target.name.toLowerCase();
    const start = currentPeriod?.startDate || '';
    const end = currentPeriod?.endDate || '9999-12-31';
    return timeEntries
      .filter(e => (e.person || '').toLowerCase() === lc)
      .filter(e => e.date && e.date >= start && e.date <= end)
      .reduce((s, e) => s + (parseFloat(e.hours) || 0), 0);
  }, [timeEntries, target.name, currentPeriod]);

  // ── Today's shoots on plate ───────────────────────────────────────────
  const todayJobs = useMemo(() => {
    return activePlateJobs.filter(j => {
      const dates = j.shootDates || (j.shootDate ? [j.shootDate] : []);
      return dates.includes(todayStr);
    });
  }, [activePlateJobs, todayStr]);

  // ── Upcoming shoots (next 7 days) on plate ────────────────────────────
  const upcomingShoots = useMemo(() => {
    const weekFromNow = toLocalDateStr(new Date(Date.now() + 7 * 86400000));
    return activePlateJobs
      .filter(j => j.shootDate && j.shootDate > todayStr && j.shootDate <= weekFromNow)
      .sort((a, b) => (a.shootDate || '').localeCompare(b.shootDate || ''));
  }, [activePlateJobs, todayStr]);

  // ── Action items split by the role the user plays on each job ────────
  // Each action lands in exactly one column based on the user's role:
  //   📸 shoot     → Producer if they produce, else Originator (referred out)
  //   📦 delivery  → Post-Producer if they post, else Producer, else Originator
  // Within each column, items are sorted ascending by date.
  const { actionByRole, actionItemsCount } = useMemo(() => {
    const groups = { orig: [], prod: [], post: [] };
    if (!target.name) return { actionByRole: groups, actionItemsCount: 0 };
    const lc = target.name.toLowerCase();

    const rolesOnJob = (j) => {
      const r = { orig: false, prod: false, post: false };
      if (!j.calcSnapshot) return r;
      try {
        const snap = normalizeSnapshot(
          typeof j.calcSnapshot === 'string' ? JSON.parse(j.calcSnapshot) : j.calcSnapshot
        );
        getAllEarnings(snap)
          .filter(e => (e.name || '').toLowerCase() === lc)
          .forEach(e => { if (r[e.role] !== undefined) r[e.role] = true; });
      } catch { /* ignore bad snapshots */ }
      return r;
    };

    const pickGroup = (actionType, roles) => {
      if (actionType === 'shoot') {
        if (roles.prod) return 'prod';
        return 'orig';
      }
      // delivery
      if (roles.post) return 'post';
      if (roles.prod) return 'prod';
      return 'orig';
    };

    const nowMs = Date.now();
    activePlateJobs.forEach((j) => {
      const roles = rolesOnJob(j);
      // Upcoming deliveries (5 days)
      if (j.deliveryDate && !['delivered', 'invoiced', 'paid'].includes(j.status)) {
        const diff = Math.round((new Date(j.deliveryDate + 'T12:00') - nowMs) / 86400000);
        if (diff >= 0 && diff <= 5) {
          const g = pickGroup('delivery', roles);
          groups[g].push({
            icon: '📦',
            label: j.title || j.clientName || 'Untitled',
            sub: diff === 0 ? 'delivery today' : `delivery in ${diff}d`,
            jobId: j.id,
            sortDate: j.deliveryDate,
          });
        }
      }
      // Upcoming shoots (7 days)
      if (!['delivered', 'invoiced', 'paid'].includes(j.status)) {
        const dates = j.shootDates || (j.shootDate ? [j.shootDate] : []);
        const nextDate = dates.filter(sd => sd > todayStr).sort()[0];
        if (nextDate) {
          const diff = Math.round((new Date(nextDate + 'T12:00') - nowMs) / 86400000);
          if (diff > 0 && diff <= 7) {
            const g = pickGroup('shoot', roles);
            groups[g].push({
              icon: '📸',
              label: j.title || j.clientName || 'Untitled',
              sub: `shoot in ${diff}d`,
              jobId: j.id,
              sortDate: nextDate,
            });
          }
        }
      }
    });

    Object.keys(groups).forEach(k => {
      groups[k].sort((a, b) => (a.sortDate || '9999').localeCompare(b.sortDate || '9999'));
    });
    const total = groups.orig.length + groups.prod.length + groups.post.length;
    return { actionByRole: groups, actionItemsCount: total };
  }, [activePlateJobs, todayStr, target.name]);

  // ── Earnings by category (Originator / Prod Lead-Hourly / Post …) ────
  // For a specific user, "by stage" is less actionable than "by role they
  // earn in" — it tells them where their money comes from. Counts unique
  // jobs per category (a user can be in multiple categories on the same
  // job, e.g. originator AND prod lead).
  const categoryBreakdown = useMemo(() => {
    if (!target.name) return [];
    const lc = target.name.toLowerCase();
    const totals = Object.fromEntries(CATEGORY_DEFS.map(c => [c.key, { earned: 0, jobIds: new Set() }]));
    activePlateJobs.forEach(j => {
      if (!j.calcSnapshot) return;
      let snap;
      try {
        snap = typeof j.calcSnapshot === 'string' ? JSON.parse(j.calcSnapshot) : j.calcSnapshot;
        snap = normalizeSnapshot(snap);
      } catch {
        return;
      }
      getAllEarnings(snap)
        .filter(e => (e.name || '').toLowerCase() === lc)
        .forEach(e => {
          let key;
          if (e.role === 'orig') key = 'orig';
          else if (e.role === 'prod') key = e.type === 'lead' ? 'prod_lead' : 'prod_hourly';
          else if (e.role === 'post') key = e.type === 'lead' ? 'post_lead' : 'post_hourly';
          if (key && totals[key]) {
            totals[key].earned += e.earned || 0;
            totals[key].jobIds.add(j.id);
          }
        });
    });
    const grandTotal = Object.values(totals).reduce((s, v) => s + v.earned, 0);
    return CATEGORY_DEFS
      .map(c => ({
        key: c.key,
        label: c.label,
        color: c.color,
        earned: totals[c.key].earned,
        count: totals[c.key].jobIds.size,
        pct: grandTotal > 0 ? Math.max(2, Math.round((totals[c.key].earned / grandTotal) * 100)) : 0,
      }))
      .filter(c => c.count > 0);
  }, [activePlateJobs, target.name]);

  const totalEarnedOnPlate = useMemo(
    () => categoryBreakdown.reduce((s, d) => s + d.earned, 0),
    [categoryBreakdown]
  );

  // ── Stat boxes ────────────────────────────────────────────────────────
  // "Earned (allocated)" sums the user's allocated earnings across ALL
  // their active jobs — i.e. money that's been promised by calc but not
  // necessarily paid yet.
  const statBoxes = [
    { label: 'Active Jobs', value: String(activePlateJobs.length), color: 'text.primary' },
    { label: 'Earned (so far)', value: fmtC(earnedSoFar), color: '#13B5EA' },
    { label: 'Hours · Period', value: hoursThisPeriod.toFixed(1), color: '#A78BFA' },
    { label: 'Action Items', value: String(actionItemsCount), color: actionItemsCount ? '#F59E0B' : 'text.primary' },
  ];

  // ── Soft warning if profile not linked to a roster member ────────────
  // Plate matching falls back to name when rosterId is unavailable, so the
  // view still works — but pay-period hours/earnings rely on exact name
  // matches against time_entries.person and pay_period_payments.person_name,
  // which is fragile without a confirmed link.
  const showUnlinkedWarning = profile && target.name && !target.linked;

  return (
    <Box>
      {showUnlinkedWarning && (
        <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            {profile.fullName || profile.email} is not linked to a roster member by email.{' '}
            <Link component={RouterLink} to="/settings?tab=team" sx={{ fontSize: '0.8rem' }}>
              Link in Settings → Team
            </Link>
            . Plate matches by name only; period numbers may be incomplete.
          </Typography>
        </Alert>
      )}
      {/* Stat Row */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        {statBoxes.map((stat) => (
          <Grid key={stat.label} size={{ xs: 6, sm: true }}>
            <Paper sx={{ p: 1.5, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ ...monoSx, fontSize: '0.6rem', textTransform: 'uppercase' }}>
                {stat.label}
              </Typography>
              <Typography sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.6rem', lineHeight: 1.2, color: stat.color }}>
                {stat.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Period meta line */}
      {currentPeriod?.startDate && (
        <Typography variant="caption" color="text.secondary" sx={{ ...monoSx, fontSize: '0.65rem', mb: 1.5, display: 'block' }}>
          Period: {currentPeriod.startDate} → {currentPeriod.endDate} ({periodIsOpen ? 'open' : 'closed'})
        </Typography>
      )}

      {/* Action Required + Today's Shoots */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Action Required</Typography>
            {actionItemsCount === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ ...monoSx, fontSize: '0.75rem' }}>
                Plate is clear
              </Typography>
            ) : (
              <Grid container spacing={1.5}>
                {[
                  { key: 'orig', label: 'Originator', color: '#F472B6' },
                  { key: 'prod', label: 'Producer', color: '#13B5EA' },
                  { key: 'post', label: 'Post-Producer', color: '#10B981' },
                ].map((col) => (
                  <Grid key={col.key} size={{ xs: 12, sm: 4 }}>
                    <Typography sx={{ ...monoSx, fontSize: '0.6rem', color: col.color, textTransform: 'uppercase', mb: 0.5, letterSpacing: '0.05em' }}>
                      {col.label}
                    </Typography>
                    <Divider sx={{ mb: 0.5, borderColor: col.color, opacity: 0.3 }} />
                    {actionByRole[col.key].length === 0 ? (
                      <Typography sx={{ ...monoSx, fontSize: '0.7rem', color: 'text.disabled', py: 0.5, px: 1 }}>
                        — none —
                      </Typography>
                    ) : (
                      actionByRole[col.key].map((item, i) => (
                        <Box
                          key={i}
                          onClick={() => item.jobId && navigateToJob(navigate, item.jobId)}
                          sx={{ py: { xs: 1, md: 0.5 }, px: 1, cursor: item.jobId ? 'pointer' : 'default', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1 }}
                        >
                          <Typography variant="body2" sx={{ fontSize: '0.78rem', lineHeight: 1.25 }}>
                            {item.icon} {item.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ ...monoSx, fontSize: '0.65rem', display: 'block', pl: '1.4em' }}>
                            {item.sub}
                          </Typography>
                        </Box>
                      ))
                    )}
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Today&apos;s Shoots</Typography>
            {todayJobs.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ ...monoSx, fontSize: '0.75rem' }}>No shoots today</Typography>
            ) : (
              todayJobs.map((j, i) => (
                <Box key={j.id}>
                  <Box
                    onClick={() => navigateToJob(navigate, j.id)}
                    sx={{ cursor: 'pointer', py: 1.5, '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1, px: 1 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Chip size="small" label={j.status} sx={{ fontSize: '0.6rem', height: 20, bgcolor: STATUS_COLORS[j.status] || '#666', color: '#fff' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{j.title || j.clientName || 'Untitled'}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ ...monoSx, fontSize: '0.7rem' }}>
                      {j.clientName}{j.location ? ` · ${j.location}` : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                      <Typography sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.2rem', color: '#13B5EA' }}>
                        {fmtC(earningsForOnJob(j, target.name))}
                      </Typography>
                    </Box>
                  </Box>
                  {i < todayJobs.length - 1 && <Divider />}
                </Box>
              ))
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Active by Stage + Upcoming Shoots */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Earned by Category</Typography>
            {categoryBreakdown.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ ...monoSx, fontSize: '0.75rem' }}>No earnings allocated yet</Typography>
            ) : (
              categoryBreakdown.map((d) => (
                <Box key={d.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: '3px' }}>
                  <Typography sx={{ ...monoSx, fontSize: '0.65rem', color: d.color, width: 110, textAlign: 'right', flexShrink: 0 }}>
                    {d.label}
                  </Typography>
                  <Box sx={{ flex: 1, height: 6, bgcolor: 'action.hover', borderRadius: '3px', overflow: 'hidden' }}>
                    <Box sx={{ width: `${d.pct}%`, height: '100%', bgcolor: d.color, borderRadius: '3px', transition: 'width 0.3s' }} />
                  </Box>
                  <Typography sx={{ ...monoSx, fontSize: '0.65rem', color: d.color, width: 55, textAlign: 'right', flexShrink: 0 }}>
                    {fmtC(d.earned)}
                  </Typography>
                  <Typography sx={{ ...monoSx, fontSize: '0.6rem', color: 'text.disabled', width: 14, flexShrink: 0 }}>
                    {d.count}
                  </Typography>
                </Box>
              ))
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="overline" color="text.secondary">Upcoming Shoots</Typography>
              <Link component={RouterLink} to="/calendar" underline="hover" variant="caption">Calendar</Link>
            </Box>
            {upcomingShoots.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ ...monoSx, fontSize: '0.75rem' }}>No shoots in the next 7 days</Typography>
            ) : (
              upcomingShoots.map((job, i) => (
                <Box key={job.id}>
                  <Box onClick={() => navigateToJob(navigate, job.id)} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1, px: 1 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: 'primary.main', opacity: 0.15, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Typography variant="caption" color="primary" sx={{ fontWeight: 700, lineHeight: 1, fontSize: '0.75rem' }}>
                        {new Date(job.shootDate + 'T00:00:00').getDate()}
                      </Typography>
                      <Typography sx={{ fontSize: '0.55rem', color: 'primary.main', textTransform: 'uppercase' }}>
                        {new Date(job.shootDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' })}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem' }}>{job.title || job.clientName || 'Untitled'}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{job.location || job.jobType || '—'}</Typography>
                    </Box>
                    <Chip size="small" label={job.status} sx={{ fontSize: '0.6rem', height: 18, bgcolor: STATUS_COLORS[job.status] || '#666', color: '#fff' }} />
                  </Box>
                  {i < upcomingShoots.length - 1 && <Divider />}
                </Box>
              ))
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
